import React, { useState, useEffect, SyntheticEvent } from "react";
import { useTranslation, useI18next } from "gatsby-plugin-react-i18next";
import { RangePicker } from "components/DatePicker/RangePicker/RangePicker";
import InputDateRangePicker from "components/DateRangeInput/InputDateRangePicker";
import dayjs from "utils/dayjs";
import type { Dayjs } from "dayjs";
import OutsideClickHandler from "react-outside-click-handler";
import {
  APP_DATE_WITH_DAYNAME_FORMAT,
  DATEPICKER_DATE_FORMAT,
  ISO_FORMAT,
} from "store/constants/date";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { type DateRateFomatted } from "apis/calendarRates";

dayjs.extend(customParseFormat);

type InputDateRangeDropdownProps = {
  startDate: Dayjs | string;
  endDate: Dayjs | string;
  fullBorderInput: boolean;
  setDateValues: (dates: {
    startDate: Dayjs | string;
    endDate: Dayjs | string;
  }) => void;
  format?: (lang: string) => string;
  withIcon: boolean;
  viewRateBlock?: boolean;
  dateRate: Record<string, DateRateFomatted> | null;
  note?: string;
};

export const InputDateRangeDropdown = ({
  startDate,
  endDate,
  fullBorderInput,
  setDateValues,
  format,
  withIcon,
  viewRateBlock,
  dateRate,
  note,
}: InputDateRangeDropdownProps) => {
  const { t } = useTranslation("homepage");
  // @ts-ignore
  const { language }: { language: "en" | "ja" | "zh" } = useI18next();
  dayjs.locale(language === "zh" ? "zh-cn" : language);
  const finalFormat = format?.(language) || DATEPICKER_DATE_FORMAT[language];
  const [visible, setVisible] = useState(false);
  const [focusInput, setFocusInput] = useState<"startDate" | "endDate">(
    "startDate"
  );
  const [startDateValue, setStartDateValue] = useState(
    dayjs(startDate).format(finalFormat)
  );
  const [endDateValue, setEndDateValue] = useState(
    dayjs(endDate).format(finalFormat)
  );

  const onStartDateClick = () => {
    setFocusInput("startDate");
    setVisible(true);
  };

  const onEndDateClick = () => {
    setFocusInput("endDate");
    setVisible(true);
  };

  const onDatesChange = (dates: {
    startDate: Dayjs | string;
    endDate: Dayjs | string;
  }) => {
    setDateValues(dates);
    setVisible(false);
  };

  const handleDropdownVisibility = () => setVisible(false);

  const blockPropagation = (e: SyntheticEvent) => e.stopPropagation();

  // When only startdate is selcted and dropdown is closed
  // set endDate = startDate +1 and set the new dates(startDate & endDate) in the calendar
  useEffect(() => {
    if (!Boolean(endDateValue) && !visible) {
      if (language === "en" && format) {
        setEndDateValue(
          dayjs(startDateValue)
            .add(1, "day")
            .format(APP_DATE_WITH_DAYNAME_FORMAT)
        );
      } else {
        setEndDateValue(
          dayjs(startDateValue, finalFormat, language)
            .add(1, "day")
            .format(finalFormat)
        );
      }
    }
    const newDates = {
      startDate:
        language === "en"
          ? dayjs(startDateValue).format(ISO_FORMAT)
          : dayjs(startDateValue, ISO_FORMAT, "en").format(ISO_FORMAT),
      endDate:
        language === "en"
          ? dayjs(endDateValue).format(ISO_FORMAT)
          : dayjs(endDateValue, ISO_FORMAT, "en").format(ISO_FORMAT),
    };
    setDateValues(newDates);
  }, [endDateValue, startDateValue, visible, language, format]);

  return (
    <div className="w-full h-full relative">
      <InputDateRangePicker
        startDateLabel={t("Check-in")}
        startDateValue={startDateValue}
        startDateClick={onStartDateClick}
        endDateValue={endDateValue}
        endDateLabel={t("Check-out")}
        endDateClick={onEndDateClick}
        fullBorder={fullBorderInput}
        withIcon={withIcon}
        viewRateBlock={viewRateBlock}
      />

      {visible && (
        <div data-testid="outside-click-handler">
          <OutsideClickHandler onOutsideClick={handleDropdownVisibility}>
            <div
              className="transition-all duration-170 ease-in absolute top-full z-10"
              onClick={blockPropagation}>
              <RangePicker
                focusInput={focusInput}
                startDate={dayjs(startDate)}
                endDate={dayjs(endDate)}
                onChange={onDatesChange}
                setStartDateValue={setStartDateValue}
                setEndDateValue={setEndDateValue}
                orientation="horizontal"
                format={format}
                dateRate={dateRate}
                note={note}
              />
            </div>
          </OutsideClickHandler>
        </div>
      )}
    </div>
  );
};
